import { all, create, factory, MathJsInstance } from 'mathjs';

/**
 * Custom mathjs functions registered via the factory pattern.
 * These are first-class mathjs functions — recognized by the parser,
 * available in autocomplete (when included in allowedFunctions), and
 * evaluated natively without scope injection.
 *
 * Each function enforces its own business rules and throws descriptive
 * errors when constraints are violated. These errors propagate through
 * `evaluateFormula` / `evaluateWithCustomFunctions` and are surfaced
 * in the UI via the evaluation result's `error` / `errorKey` fields.
 *
 * Usage:
 * - Add function names to `allowedFunctions` in your `FormulaEditorConfig`
 *   to enable them in the formula editor.
 * - Use `customMathInstance.evaluate(expression)` or the exported
 *   `evaluateWithCustomFunctions` helper to evaluate expressions.
 */

function assertFinite(value: unknown, fnName: string, paramName: string): asserts value is number {
  if (typeof value !== 'number' || !isFinite(value)) {
    throw new Error(`${fnName}: ${paramName} must be a finite number, got ${value}`);
  }
}

/**
 * Custom mathjs instance with additional conversion/calculation functions.
 * Extends the full mathjs `all` bundle with:
 * - `slopeShiftFn(x, slope?, shift?)` — affine transform
 * - `minMaxFn(x, minRead, maxRead, minDisplay, maxDisplay)` — linear interpolation
 * - `deltaFn(...vals)` — spread: max - min
 */
export const customMathInstance: MathJsInstance = create({
  ...all,

  createSlopeShiftFn: factory('slopeShiftFn', [], () =>
    /**
     * Affine transform: x * slope + shift.
     *
     * Business rules:
     * - x, slope, shift must be finite numbers
     */
    (x: number, slope = 1, shift = 0): number => {
      assertFinite(x, 'slopeShiftFn', 'x');
      assertFinite(slope, 'slopeShiftFn', 'slope');
      assertFinite(shift, 'slopeShiftFn', 'shift');
      return x * slope + shift;
    }
  ),

  createMinMaxFn: factory('minMaxFn', [], () =>
    /**
     * Scale value from read range [minRead, maxRead] to display range [minDisplay, maxDisplay].
     *
     * Business rules:
     * - Requires exactly 5 arguments
     * - All arguments must be finite numbers
     * - minRead < maxRead (strict — prevents division by zero)
     * - minDisplay ≤ maxDisplay
     */
    (x: number, minRead: number, maxRead: number, minDisplay: number, maxDisplay: number): number => {
      assertFinite(x, 'minMaxFn', 'x');
      assertFinite(minRead, 'minMaxFn', 'minRead');
      assertFinite(maxRead, 'minMaxFn', 'maxRead');
      assertFinite(minDisplay, 'minMaxFn', 'minDisplay');
      assertFinite(maxDisplay, 'minMaxFn', 'maxDisplay');
      if (minRead >= maxRead) {
        throw new Error('minMaxFn: minRead must be < maxRead');
      }
      if (minDisplay > maxDisplay) {
        throw new Error('minMaxFn: minDisplay must be ≤ maxDisplay');
      }
      return minDisplay + ((x - minRead) * (maxDisplay - minDisplay)) / (maxRead - minRead);
    }
  ),

  createDeltaFn: factory('deltaFn', [], () =>
    /**
     * Spread of values: max - min.
     *
     * Business rules:
     * - Requires at least 1 argument
     * - All arguments must be finite numbers
     */
    (...vals: number[]): number => {
      if (vals.length === 0) {
        throw new Error('deltaFn: requires at least 1 argument');
      }
      vals.forEach((v, i) => assertFinite(v, 'deltaFn', `argument[${i}]`));
      return Math.max(...vals) - Math.min(...vals);
    }
  )
});

/**
 * Names of custom functions registered in the custom mathjs instance.
 * Use these to include in `FormulaEditorConfig.allowedFunctions`.
 */
export const CUSTOM_FUNCTION_NAMES = ['slopeShiftFn', 'minMaxFn', 'deltaFn'] as const;

/**
 * Evaluates an expression using the custom mathjs instance (includes custom functions).
 * This is a convenience wrapper around `customMathInstance.evaluate`.
 *
 * @param expression - mathjs expression string
 * @param scope - Optional variable scope (alias → value map)
 * @returns The evaluation result
 */
export function evaluateWithCustomFunctions(expression: string, scope?: Record<string, unknown>): unknown {
  return scope
    ? customMathInstance.evaluate(expression, scope)
    : customMathInstance.evaluate(expression);
}
